// src/components/chat/ChatbotWidget.jsx
import React, { useState } from 'react';
import { IconMessageCircle } from '@tabler/icons-react';
import stringSimilarity from 'string-similarity';

// FAQ data as provided
const faqSections = [
  {
    title: "Orders & Shipping",
    items: [
      {
        question: "How Do I Track My Order?",
        answer: `1. Log In to Your Account.\n2. Click on “My Account” in the Top Menu and Select “My Order.”\n3. Find the Order You Want To Track and Click “Track Order.”\n4. You’ll See the Current Status, Estimated Delivery Date, and Tracking Number.`,
      },
      {
        question: "What Are The Shipping Options And Costs?",
        answer: `We offer both Standard and Express shipping. The cost depends on your location and the weight of your order. You’ll see exact shipping charges at checkout.`,
      },
      {
        question: "Can I Change My Shipping Address After Placing An Order?",
        answer: `Yes, as long as your order hasn’t shipped yet. You can update your address from your account page or by contacting our support team.`,
      },
      {
        question: "What Happens If My Order Is Delayed Or Lost?",
        answer: `If your order is delayed, we’ll do our best to expedite it. If it’s lost, we’ll replace or refund your purchase after confirming with the carrier.`,
      },
    ],
  },
  {
    title: "Designing & Customization",
    items: [
      {
        question: "How Do I Upload My Own Design?",
        answer: `You can upload your own images or graphics by clicking “Upload” in our design tool. Accepted file formats are PNG, JPG, or SVG.`,
      },
      {
        question: "What File Formats Are Supported?",
        answer: `Our system supports PNG, JPG, JPEG, GIF, and SVG. For best results, use high-resolution images (300 DPI or above).`,
      },
      {
        question: "Do You Offer Free Design Templates?",
        answer: `Yes, we have a library of free design templates that you can customize. Simply choose a template and edit it with your text or images.`,
      },
      {
        question: "Can I Edit My Design After Placing An Order?",
        answer: `Unfortunately, once an order is placed, the design can’t be changed. Please ensure your design is final before checkout.`,
      },
    ],
  },
  {
    title: "Payments & Refunds",
    items: [
      {
        question: "What Payment Methods Do You Accept?",
        answer: `We accept all major credit cards, PayPal, and select local payment methods depending on your region.`,
      },
      {
        question: "How Do I Apply A Promo Code?",
        answer: `Enter your promo code at checkout in the “Discount Code” field. The discount will apply if the code is valid.`,
      },
      {
        question: "Can I Cancel My Order And Get A Refund?",
        answer: `You can cancel your order for a full refund if it hasn’t entered production. Once production begins, cancellations or refunds may not be available.`,
      },
    ],
  },
  {
    title: "Technical Issues",
    items: [
      {
        question: "I’m Having Trouble Uploading My Design",
        answer: `If you’re having trouble uploading your design, try clearing your browser cache or using a different browser. If the issue persists, contact our support team.`,
      },
      {
        question: "Why Is My Design Blurry Or Pixelated?",
        answer: `For best results, use high-resolution images (300 DPI or above). Blurry or pixelated designs are usually due to low-quality images.`,
      },
      {
        question: "Why Is My Design Not Centered?",
        answer: `Our design tool automatically centers your design. If it’s not centered, try resizing or repositioning your design within the print area.`,
      },
    ],
  },
  {
    title: "Account & Privacy",
    items: [
      {
        question: "How Do I Create An Account?",
        answer: `To create an account, click “Sign Up” in the top menu. Enter your email and password, or sign up with Google or Facebook.`,
      },
      {
        question: "How Do I Change My Password?",
        answer: `You can change your password from your account settings. Click “My Account” and select “Change Password.”`,
      },
      {
        question: "Is My Personal Information Secure?",
        answer: `Yes, we take data security seriously. Your personal information is encrypted and stored securely. We never share your data with third parties.`,
      },
    ],
  },
];

// Enhanced conversation flow with an "askQuestion" node
const conversationFlow = {
  start: {
    message: "Hi! I'm here to help. What would you like to do?",
    options: [
      { label: "Track My Order", next: "trackOrder" },
      { label: "Help & Assist", next: "helpAssist" },
      { label: "Billing & Payment", next: "billing" },
      { label: "Talk to a Human", next: "talkHuman" },
    ],
  },
  trackOrder: {
    message:
      "To track your order, please visit the 'Order History' page and enter your Order ID. If you have any issues, feel free to talk to a human.",
    options: [
      { label: "Go Back", next: "start" },
      { label: "Talk to a Human", next: "talkHuman" },
    ],
  },
  helpAssist: {
    message:
      "For help & assistance, check out our FAQ page or ask a question below.",
    options: [
      { label: "Ask a Question", next: "askQuestion" },
      { label: "Go Back", next: "start" },
      { label: "Talk to a Human", next: "talkHuman" },
    ],
  },
  billing: {
    message:
      "For billing & payment info, see your 'Account' page or contact our billing department directly.",
    options: [
      { label: "Go Back", next: "start" },
      { label: "Talk to a Human", next: "talkHuman" },
    ],
  },
  talkHuman: {
    message:
      "Sure! One moment while I connect you with a human. Alternatively, you can call us at +1 (555) 123-4567 or email support@company.com",
    options: [{ label: "Go Back", next: "start" }],
  },
  askQuestion: {
    message: "Please type your question related to our FAQs:",
    input: true,
  },
};

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentNodeKey, setCurrentNodeKey] = useState("start");
  const [conversation, setConversation] = useState([
    { from: "bot", text: conversationFlow.start.message },
  ]);
  const [userInput, setUserInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Toggle the chat window open/close
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
  };

  // Clear the entire conversation and reset the chat
  const clearChat = () => {
    setConversation([{ from: "bot", text: conversationFlow.start.message }]);
    setCurrentNodeKey("start");
  };

  // Handle clicks on decision-tree options
  const handleOptionClick = (nextKey) => {
    setCurrentNodeKey(nextKey);
    const nextNode = conversationFlow[nextKey];
    setConversation((prev) => [
      ...prev,
      { from: "bot", text: nextNode.message },
    ]);
  };

  // Fuzzy match FAQ answer using string-similarity
  const findFAQAnswer = (query) => {
    let bestMatch = { rating: 0, answer: null };
    faqSections.forEach((section) => {
      section.items.forEach((item) => {
        const rating = stringSimilarity.compareTwoStrings(
          query.toLowerCase(),
          item.question.toLowerCase()
        );
        if (rating > bestMatch.rating) {
          bestMatch = { rating, answer: item.answer };
        }
      });
    });
    // Use a threshold (e.g., 0.3) to determine a valid match
    return bestMatch.rating >= 0.3 ? bestMatch.answer : null;
  };

  // Call Hugging Face Inference API for an AI-generated answer using Q&A endpoint
  const callHuggingFaceAPI = async (inputText) => {
    // Using the router endpoint for the distilbert model for question answering
    const API_URL = "https://router.huggingface.co/hf-inference/models/distilbert/distilbert-base-cased-distilled-squad";

    const API_KEY = process.env.REACT_APP_API_CALLING_HUG_FACE;
    const defaultContext =
      "Orders & Shipping: Log In to your account, click on 'My Account' and select 'My Order' to track your order. " +
      "Designing & Customization: You can upload your own design using our design tool. Supported file formats include PNG, JPG, and SVG. " +
      "Payments & Refunds: We accept major credit cards, PayPal, and local payment methods. Enter your promo code at checkout if valid. " +
      "Technical Issues: For uploading issues, clear your browser cache or try a different browser. " +
      "Account & Privacy: You can change your password from your account settings under 'Change Password'. Your information is secure.";

    const payload = {
      inputs: {
        question: inputText,
        context: defaultContext,
      },
    };

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${API_KEY}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      // For question answering, the expected answer is in data.answer
      if (data && data.answer) {
        return data.answer;
      }
      return "Sorry, I couldn't generate a response.";
    } catch (error) {
      console.error("Hugging Face API error:", error);
      return "Sorry, I couldn't process your request at the moment.";
    }
  };

  // Handle submission of free-text input
  const handleInputSubmit = async (e) => {
    e.preventDefault();
    const trimmedInput = userInput.trim();
    if (!trimmedInput) return;

    // Add user's message to conversation
    setConversation((prev) => [
      ...prev,
      { from: "user", text: trimmedInput },
    ]);

    if (currentNodeKey === "askQuestion") {
      // First check if an FAQ answer exists using fuzzy matching
      const faqAnswer = findFAQAnswer(trimmedInput);
      if (faqAnswer) {
        setConversation((prev) => [
          ...prev,
          { from: "bot", text: faqAnswer },
        ]);
        // Return to start after answering
        setCurrentNodeKey("start");
        setConversation((prev) => [
          ...prev,
          { from: "bot", text: conversationFlow.start.message },
        ]);
      } else {
        // No FAQ answer found—use the Hugging Face API for an AI-generated response
        setIsLoading(true);
        const aiAnswer = await callHuggingFaceAPI(trimmedInput);
        setIsLoading(false);
        setConversation((prev) => [
          ...prev,
          { from: "bot", text: aiAnswer },
        ]);
        // After responding, revert back to the start node
        setCurrentNodeKey("start");
        setConversation((prev) => [
          ...prev,
          { from: "bot", text: conversationFlow.start.message },
        ]);
      }
    }
    setUserInput("");
  };

  const currentNode = conversationFlow[currentNodeKey];

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end">
      {/* Chat button when closed */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="bg-[#00148c] text-white p-3 rounded-full shadow-lg flex items-center gap-2 hover:bg-[#002080]"
        >
          <IconMessageCircle size={20} />
          <span className="hidden sm:inline">Need Help?</span>
        </button>
      )}

      {/* Chat window when open */}
      {isOpen && (
        <div className="w-80 bg-white border border-gray-300 shadow-lg rounded-md overflow-hidden">
          {/* Header */}
          <div className="bg-[#00148c] text-white py-2 px-4 flex justify-between items-center">
            <h2 className="font-bold">CompanyName Assistant</h2>
            <div className="flex gap-2">
              <button onClick={clearChat} className="text-white font-bold">
                Clear
              </button>
              <button onClick={toggleChat} className="text-white font-bold">
                X
              </button>
            </div>
          </div>

          {/* Conversation messages */}
          <div className="p-3 h-64 overflow-y-auto space-y-2">
            {conversation.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.from === "bot" ? "justify-start" : "justify-end"}`}
              >
                <div
                  className={`max-w-xs break-words inline-block rounded px-3 py-2 ${msg.from === "bot" ? "bg-gray-200 text-black" : "bg-blue-500 text-white"
                    }`}
                >
                  {msg.text.split("\n").map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="max-w-xs break-words inline-block rounded px-3 py-2 bg-gray-200 text-black">
                  Loading...
                </div>
              </div>
            )}
          </div>

          {/* Input field or options */}
          <div className="p-3 border-t border-gray-300">
            {currentNode?.input ? (
              <form onSubmit={handleInputSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 border border-gray-300 rounded px-2 py-1 focus:outline-none"
                />
                <button
                  type="submit"
                  className="bg-[#FFD100] text-[#00148c] font-semibold rounded px-3 py-1 hover:bg-[#FFC300]"
                >
                  Send
                </button>
              </form>
            ) : (
              currentNode?.options?.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleOptionClick(opt.next)}
                  className="block w-full text-left bg-[#FFD100] text-[#00148c] font-semibold rounded px-3 py-2 mb-2 hover:bg-[#FFC300]"
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
